import axios from 'axios';
import { AppLogger } from '@/logger';
import type {
  CreateSupportRequestDto,
  SupportRequestCategoryDto,
  SupportRequestDepartmentDto,
  SupportRequestSubcategoryDto,
  SupportRequestTypeDto,
} from '@/castapi';
import { apiConfig } from '@/utils';
import { getErrorMessage, getSharedFileApi, getSupportRequestApi } from '@/castapi/helpers';
import type { IActionParams } from '@/store/modules/index';

const logger = new AppLogger('support state');

interface SupportRequestFile {
  fileId: string;
  fileName: string;
}

// The upload session is not in the generated castapi client yet, so it is called directly - the
// same pattern adminMaintenanceWindows and login already use for routes added after a generation.
const uploadSessionUrl = `${apiConfig.basePath}/api/share-file/upload-session`;
const authHeaders = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

interface UploadSession {
  instanceId: string;
  instanceToken: string;
}

const getSseEventSource = (accessToken: string, session: UploadSession) =>
  new EventSource(
    `${apiConfig.basePath}/api/single/share-file-notifications/events?token=${accessToken}` +
      `&id=${session.instanceId}&instanceToken=${encodeURIComponent(session.instanceToken)}`,
  );

interface ISupportRequestState {
  isLoading: boolean;
  error?: string;
  supportRequestTypes: SupportRequestTypeDto[];
  supportRequestDepartments: SupportRequestDepartmentDto[];
  supportRequestCategories: SupportRequestCategoryDto[];
  supportRequestSubcategories: SupportRequestSubcategoryDto[];
  shareFileUploadLink?: string;
  supportRequestFiles: SupportRequestFile[];
  eventSource?: EventSource;
  uploadSession?: UploadSession;
}

const initialState = (): ISupportRequestState => ({
  isLoading: false,
  error: undefined,
  supportRequestTypes: [],
  supportRequestDepartments: [],
  supportRequestCategories: [],
  supportRequestSubcategories: [],
  shareFileUploadLink: undefined,
  supportRequestFiles: [],
  eventSource: undefined,
  uploadSession: undefined,
});

type ActionParams = IActionParams<ISupportRequestState>;

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    RESET_STATE(state: ISupportRequestState): void {
      Object.assign(state, initialState());
    },
    ERROR(state: ISupportRequestState, error: Error): void {
      state.error = getErrorMessage(error);
      state.isLoading = false;
    },
    SUPPORT_REQUEST_TYPES_LOADING(state: ISupportRequestState): void {
      state.error = undefined;
      state.isLoading = true;
      state.supportRequestTypes = [];
    },
    SUPPORT_REQUEST_DEPARTMENTS_LOADING(state: ISupportRequestState): void {
      state.error = undefined;
      state.isLoading = true;
      state.supportRequestDepartments = [];
    },
    SUPPORT_REQUEST_CATEGORIES_LOADING(state: ISupportRequestState): void {
      state.error = undefined;
      state.isLoading = true;
      state.supportRequestCategories = [];
    },
    SUPPORT_REQUEST_SUBCATEGORIES_LOADING(state: ISupportRequestState): void {
      state.error = undefined;
      state.isLoading = true;
      state.supportRequestSubcategories = [];
    },
    SUPPORT_REQUEST_TYPES_LOADED(state: ISupportRequestState, payload: SupportRequestTypeDto[]): void {
      state.isLoading = false;
      state.supportRequestTypes = payload;
    },
    SUPPORT_REQUEST_DEPARTMENTS_LOADED(state: ISupportRequestState, payload: SupportRequestDepartmentDto[]): void {
      state.isLoading = false;
      state.supportRequestDepartments = payload;
    },
    SUPPORT_REQUEST_CATEGORIES_LOADED(state: ISupportRequestState, payload: SupportRequestCategoryDto[]): void {
      state.isLoading = false;
      state.supportRequestCategories = payload;
    },
    SUPPORT_REQUEST_SUBCATEGORIES_LOADED(state: ISupportRequestState, payload: SupportRequestSubcategoryDto[]): void {
      state.isLoading = false;
      state.supportRequestSubcategories = payload;
    },
    SUPPORT_REQUEST_CREATING(state: ISupportRequestState): void {
      state.error = undefined;
      state.isLoading = true;
    },
    SUPPORT_REQUEST_CREATED(state: ISupportRequestState): void {
      state.isLoading = false;
    },
    SHARE_FILE_UPLOAD_LINK_CREATING(state: ISupportRequestState): void {
      state.shareFileUploadLink = undefined;
      state.error = undefined;
      state.isLoading = true;
    },
    SHARE_FILE_UPLOAD_LINK_CREATED(state: ISupportRequestState, link: string): void {
      state.isLoading = false;
      state.shareFileUploadLink = link;
    },
    SUPPORT_REQUEST_FILE_DELETING(state: ISupportRequestState): void {
      state.isLoading = true;
    },
    SUPPORT_REQUEST_FILE_DELETED(state: ISupportRequestState, fileId: string): void {
      state.isLoading = false;
      state.supportRequestFiles = state.supportRequestFiles.filter(file => file.fileId !== fileId);
    },
    HANDLE_MESSAGE(state: ISupportRequestState, data: { fileId; fileName; operationName }): void {
      if (data.operationName === 'Upload') {
        const { fileId, fileName } = data;
        state.supportRequestFiles.push({ fileId, fileName });
      } else if (data.operationName === 'Delete') {
        const { fileId } = data;
        state.supportRequestFiles = state.supportRequestFiles.filter(file => file.fileId !== fileId);
      }
    },
    UPLOAD_SESSION_CREATED(state: ISupportRequestState, session: UploadSession): void {
      state.uploadSession = session;
    },
    SSE_CONNECTED(state: ISupportRequestState, eventSource: EventSource): void {
      state.eventSource = eventSource;
    },
    SSE_DISCONNECTED(state: ISupportRequestState): void {
      state.eventSource = undefined;
    },
  },
  actions: {
    async loadSupportRequestTypes({ commit, rootGetters }: ActionParams): Promise<void> {
      try {
        commit('SUPPORT_REQUEST_TYPES_LOADING');
        const accessToken = rootGetters['login/accessToken'];
        const response = await getSupportRequestApi(accessToken).supportRequestControllerGetSupportRequestTypes();
        commit('SUPPORT_REQUEST_TYPES_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('loadSupportRequestTypes', error);
      }
    },

    async loadSupportRequestDepartments({ commit, rootGetters }: ActionParams): Promise<void> {
      try {
        commit('SUPPORT_REQUEST_DEPARTMENTS_LOADING');
        const accessToken = rootGetters['login/accessToken'];
        const response = await getSupportRequestApi(accessToken).supportRequestControllerGetSupportRequestDepartments();
        commit('SUPPORT_REQUEST_DEPARTMENTS_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('loadSupportRequestDepartments', error);
      }
    },

    async loadSupportRequestCategories({ commit, rootGetters }: ActionParams): Promise<void> {
      try {
        commit('SUPPORT_REQUEST_CATEGORIES_LOADING');
        const accessToken = rootGetters['login/accessToken'];
        const response = await getSupportRequestApi(accessToken).supportRequestControllerGetSupportRequestCategories();
        commit('SUPPORT_REQUEST_CATEGORIES_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('loadSupportRequestCategories', error);
      }
    },

    async loadSupportRequestSubcategories({ commit, rootGetters }: ActionParams): Promise<void> {
      try {
        commit('SUPPORT_REQUEST_SUBCATEGORIES_LOADING');
        const accessToken = rootGetters['login/accessToken'];
        const response =
          await getSupportRequestApi(accessToken).supportRequestControllerGetSupportRequestSubcategories();
        commit('SUPPORT_REQUEST_SUBCATEGORIES_LOADED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('loadSupportRequestSubcategories', error);
      }
    },

    async createSupportRequest({ commit, rootGetters }: ActionParams, request: CreateSupportRequestDto): Promise<void> {
      try {
        commit('SUPPORT_REQUEST_CREATING');
        const accessToken = rootGetters['login/accessToken'];
        await getSupportRequestApi(accessToken).supportRequestControllerCreateSupportRequest(request);
        commit('SUPPORT_REQUEST_CREATED');
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('createSupportRequest', error);
      }
    },

    async getShareFileUploadLink({ commit, rootGetters }: ActionParams, parentFolderName: string): Promise<void> {
      commit('SHARE_FILE_UPLOAD_LINK_CREATING');
      try {
        const accessToken = rootGetters['login/accessToken'];
        const response = await getSharedFileApi(accessToken).shareFileControllerGetUploadLink(parentFolderName);
        commit('SHARE_FILE_UPLOAD_LINK_CREATED', response.data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('getShareFileUploadLink', error);
      }
    },

    async deleteFileFromFileShare({ commit, rootGetters }: ActionParams, fileId: string): Promise<void> {
      try {
        commit('SUPPORT_REQUEST_FILE_DELETING');
        const accessToken = rootGetters['login/accessToken'];
        await getSharedFileApi(accessToken).shareFileControllerDeleteFile({ fileId });
        commit('SUPPORT_REQUEST_FILE_DELETED', fileId);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('deleteFileFromFileShare', error);
      }
    },

    // The instance id is issued by the server, never invented here, so that the SSE stream can tie
    // the subscription to its owner. Without a session there is nothing to subscribe to.
    async createUploadSession({ commit, rootGetters }: ActionParams): Promise<void> {
      try {
        const accessToken = rootGetters['login/accessToken'];
        const { data } = await axios.post<UploadSession>(uploadSessionUrl, {}, authHeaders(accessToken));
        commit('UPLOAD_SESSION_CREATED', data);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('createUploadSession', error);
      }
    },

    connectToSSE({ state, rootGetters, commit, dispatch }: ActionParams): void {
      try {
        const session = state.uploadSession;
        if (!session) {
          return;
        }
        const accessToken = rootGetters['login/accessToken'];
        const eventSource = getSseEventSource(accessToken, session);
        eventSource.onmessage = event => {
          const data = JSON.parse(event.data);
          commit('HANDLE_MESSAGE', data);
        };
        eventSource.addEventListener('token_not_valid', async () => {
          eventSource.close();
          await dispatch('login/refreshToken', undefined, { root: true });
          await dispatch('connectToSSE');
        });
        eventSource.onerror = () => {
          eventSource.close();
          setTimeout(() => dispatch('connectToSSE'), 30000);
        };
        commit('SSE_CONNECTED', eventSource);
      } catch (error) {
        commit('ERROR', error);
        logger.captureStoreError('connectToSSE', error);
      }
    },

    disconnectFromSSE({ state, commit }: ActionParams): void {
      if (state.eventSource) {
        state.eventSource.close();
      }
      commit('SSE_DISCONNECTED');
    },
  },
  getters: {
    isLoading: (state: ISupportRequestState): boolean => state.isLoading,
    error: (state: ISupportRequestState): undefined | string => state.error,
    supportRequestTypes: (state: ISupportRequestState): SupportRequestTypeDto[] => state.supportRequestTypes,
    supportRequestDepartments: (state: ISupportRequestState): SupportRequestDepartmentDto[] =>
      state.supportRequestDepartments,
    supportRequestCategories: (state: ISupportRequestState): SupportRequestCategoryDto[] =>
      state.supportRequestCategories,
    supportRequestSubcategories: (state: ISupportRequestState): SupportRequestSubcategoryDto[] =>
      state.supportRequestSubcategories,
    shareFileUploadLink: (state: ISupportRequestState): undefined | string => state.shareFileUploadLink,
    instanceId: (state: ISupportRequestState): undefined | string => state.uploadSession?.instanceId,
    supportRequestFiles: (state: ISupportRequestState): SupportRequestFile[] => state.supportRequestFiles,
  },
};
